import * as Sentry from '@sentry/react'
import {
  apiTransformSession,
  apiTransformContacts,
  apiTransformContributions,
  apiTransformClassrooms,
  apiTransformStudents,
  apiTransformCreateContactResponse,
  apiTransformGoal,
  apiTransformGoalWithFallback,
} from './transformers'

import type {
  APIContact,
  APIContribution,
  APIClassroom,
  APIStudent,
  APIContributionGoal,
  APIContactGoal,
  Classroom,
  Contact,
  Goal,
  Student,
  ISODate,
} from './contribution.d.ts'

import {
  type Session,
  SESSION_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  ACCESS_TOKEN_STORAGE_KEY,
} from '@/context/SessionContext'
import { getFromSessionStorage, deleteFromSessionStorage, setInSessionStorage } from '@/util/storage'
import { getSignInURL } from '@/util/helpers'
import { RegistrationClosedError } from '@/util/errors'

import { DEFAULT_CONTACT_GOAL, DEFAULT_CONTRIBUTION_GOAL } from '@/util/defaults.ts'

/**
 * Root helper function to make API requests to the contribution platform.
 * The Authorization Header is set with an API token to login, then uses the session-specific accessToken for additional API requests.
 * - the API token is scoped only to the login endpoint
 * - accessToken is set in context and sessionStorage and is valid for 1 hour
 * - if a response returns a 401 status, the session is cleared and the user is redirected to the sign_in page
 *
 * @param endpoint
 * @param method
 * @param body
 * @param contentType
 * @param attemptRefresh
 * @param authenticationType - 'api' | 'access'
 * @returns
 */
async function contributionAPI(
  endpoint: string,
  authenticationType: 'api' | 'access' = 'access',
  method?: string,
  body?: BodyInit,
  contentType?: 'json' | 'form',
  attemptRefresh: boolean = true,
): Promise<Response> {
  const session: Session | undefined = getFromSessionStorage<Session>(SESSION_STORAGE_KEY)
  const accessToken: string | undefined = getFromSessionStorage(ACCESS_TOKEN_STORAGE_KEY) || session?.accessToken

  const authToken: string =
    authenticationType === 'api' ? import.meta.env.VITE_CONTRIBUTION_PLATFORM_PROLOGUE_API_TOKEN : accessToken
  if (!authToken) {
    throw new Error('No authentication token found')
  }

  const url = `${import.meta.env.VITE_CONTRIBUTION_PLATFORM_PROLOGUE_API_URL}/${endpoint}`

  const headers: Record<string, string> = {
    Authorization: authToken,
  }

  //when sending formData via fetch, the Content-Type header MUST BE set automatically
  // or the request will fail. Do not set it to 'multipart/form-data'.
  if (contentType !== 'form') {
    headers['Content-Type'] = 'application/json'
  }

  const options: RequestInit = {
    method: method ?? 'GET',
    headers,
  }
  if (body) {
    options.body = body
  }

  let response = await fetch(url, options)

  if (response.status === 401 && attemptRefresh) {
    try {
      await refreshAccessToken()
      Sentry.logger.debug('Access token refreshed successfully. Retrying original request...')

      // Retry the original request with the new access token
      response = await contributionAPI(endpoint, authenticationType, method, body, contentType)
    } catch (error) {
      const signInURL = getSignInURL(session?.schoolSlug)
      deleteFromSessionStorage(SESSION_STORAGE_KEY)
      deleteFromSessionStorage(ACCESS_TOKEN_STORAGE_KEY)
      deleteFromSessionStorage(REFRESH_TOKEN_STORAGE_KEY)

      Sentry.logger.error('Failed to refresh access token', { error: String(error) })
      window.location.href = signInURL
    }
  }

  if (response.status === 403) {
    const errorData = await response.json()
    throw new RegistrationClosedError(errorData?.error || 'Registration Closed.')
  }

  if (response.status === 422) {
    const errorData = await response.json()
    if (errorData.error === 'Participation Not Editable.') {
      throw new RegistrationClosedError(
        'Registration is closed. Your account data, students, and invited contacts can no longer be changed.',
      )
    } else {
      throw new Error(errorData.error)
    }
  }

  return response
}

export interface LoginSessionResponse {
  access_token: string
  refresh_token: string
  event_id: number
  last_prologue_login_at: ISODate
  parent_name: string | null
  parent_slug: string
  participation_id: number
  school_name: string | null
  photo_url: string | null
  participation_editable: boolean // we don't disabled UX functionality based on this flag
}
export async function exchangeLoginToken(schoolSlug: string, loginToken: string) {
  let body = JSON.stringify({ login_token: loginToken })

  if (
    window?.location?.href &&
    (window.location.href.includes('localhost') || window.location.href.includes('amplifyapp.com'))
  ) {
    body = JSON.stringify({ parent_slug: '6grwrfvn' })
  }

  const resp = await contributionAPI(`auths/${schoolSlug}/login_with_token`, 'api', 'POST', body, 'json', false)
  if (resp.status === 200) {
    const session: LoginSessionResponse = await resp.json()

    const transformedSession = apiTransformSession(session)
    Sentry.logger.debug('Login token exchange: session transformed', {
      participationId: transformedSession.participationId,
    })
    setInSessionStorage(ACCESS_TOKEN_STORAGE_KEY, transformedSession.accessToken)
    setInSessionStorage(REFRESH_TOKEN_STORAGE_KEY, transformedSession.refreshToken)
    return transformedSession
  }
  if (resp.status === 401) {
    throw new Error('Login token has expired or is invalid')
  }
  throw new Error('Failed to exchange login token')
}

/**
 *  Login with email and password.
 * @param schoolSlug
 * @param email
 * @param password
 * @returns
 */
export async function loginWithPassword(schoolSlug: string, email: string, password: string) {
  const body = JSON.stringify({
    email,
    password,
  })

  Sentry.logger.debug(Sentry.logger.fmt`Attempting password login for ${email} at school ${schoolSlug}...`)

  const resp = await contributionAPI(`auths/${schoolSlug}/login_with_password`, 'api', 'POST', body, 'json', false)
  if (resp.status === 200) {
    const session: LoginSessionResponse = await resp.json()

    const transformedSession = apiTransformSession(session)
    setInSessionStorage(ACCESS_TOKEN_STORAGE_KEY, transformedSession.accessToken)
    setInSessionStorage(REFRESH_TOKEN_STORAGE_KEY, transformedSession.refreshToken)
    return transformedSession
  }

  throw new Error('Invalid login credentials')
}

/**
 *  Register with email and password.
 * @param schoolSlug
 * @param name
 * @param email
 * @param password
 * @returns
 */
export async function registerWithPassword(schoolSlug: string, name: string, email: string, password: string) {
  const body = JSON.stringify({
    name,
    email,
    password,
  })

  const resp = await contributionAPI(`auths/${schoolSlug}/register_with_password`, 'api', 'POST', body, 'json', false)
  if (resp.status === 200) {
    const session: LoginSessionResponse = await resp.json()

    const transformedSession = apiTransformSession(session)
    setInSessionStorage(ACCESS_TOKEN_STORAGE_KEY, transformedSession.accessToken)
    setInSessionStorage(REFRESH_TOKEN_STORAGE_KEY, transformedSession.refreshToken)
    return transformedSession
  }

  let errorMessage = ''
  try {
    const errorData = await resp.json()
    if (errorData?.error) {
      if (Array.isArray(errorData.error)) {
        errorMessage = errorData.error.join('. ')
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error
      }
    }
  } catch {
    throw new Error("Sorry, we couldn't create your account. Please try again later.")
  }

  if (errorMessage) {
    throw new Error(errorMessage)
  }

  throw new Error(`Failed to update student: ${resp.status} ${resp.statusText}`)
}

/**
 * Login with a magic link sent to email.
 * The magic link will take the user to the login_with_token flow.
 * @param schoolSlug
 * @param email
 * @returns
 */
export async function requestMagicLink(schoolSlug: string, email: string) {
  const body = JSON.stringify({
    email,
  })

  const resp = await contributionAPI(`auths/${schoolSlug}/send_login_email`, 'api', 'POST', body, 'json', false)

  if (resp.status === 204) {
    return true
  }
  return false
}

export async function sendResetPasswordEmail(schoolSlug: string, email: string): Promise<boolean> {
  const body = JSON.stringify({ email })
  const resp = await contributionAPI(
    `auths/${schoolSlug}/send_reset_password_email`,
    'api',
    'POST',
    body,
    'json',
    false,
  )
  return resp.status === 204
}

export async function resetPassword(
  schoolSlug: string,
  resetPasswordToken: string,
  password: string,
): Promise<boolean> {
  const body = JSON.stringify({ reset_password_token: resetPasswordToken, password })
  const resp = await contributionAPI(`auths/${schoolSlug}/reset_password`, 'api', 'POST', body, 'json', false)
  return resp.status === 204
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
}
export async function refreshAccessToken(): Promise<boolean> {
  const apiToken = import.meta.env.VITE_CONTRIBUTION_PLATFORM_PROLOGUE_API_TOKEN
  const refreshToken: string | undefined = getFromSessionStorage(REFRESH_TOKEN_STORAGE_KEY)
  const session: Session | undefined = getFromSessionStorage<Session>(SESSION_STORAGE_KEY)
  const schoolSlug = session?.schoolSlug

  if (!apiToken || !refreshToken || !schoolSlug) {
    throw new Error('Missing api token, refresh token or school slug found for refreshing access token')
  }

  // if we use the contributionAPI wrapper here, it could cause an infinite loop if the refresh token is also invalid and returns a 401.
  // We can simply avoid that be using fetch directly.
  const url = `${import.meta.env.VITE_CONTRIBUTION_PLATFORM_PROLOGUE_API_URL}/auths/${schoolSlug}/refresh_access_token`
  const options: RequestInit = {
    method: 'POST',
    headers: {
      Authorization: apiToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }

  const resp = await fetch(url, options)

  if (resp.status === 200) {
    const { access_token, refresh_token }: RefreshTokenResponse = await resp.json()

    setInSessionStorage(ACCESS_TOKEN_STORAGE_KEY, access_token)
    setInSessionStorage(REFRESH_TOKEN_STORAGE_KEY, refresh_token)
    return true
  }
  // OTHERWISE: the refresh token is no longer valid
  else {
    Sentry.logger.error(Sentry.logger.fmt`Failed to refresh access token: ${resp.status} ${resp.statusText}`)
    throw new Error(`Failed to refresh access token: ${resp.status} ${resp.statusText}`)
  }
}

export interface GetSchoolResponse {
  name: string
  school_logo_url: string | null
  registration_open: boolean
}
export async function getSchoolDetails(schoolSlug: string) {
  const resp = await contributionAPI(`schools/${schoolSlug}`, 'api', 'GET', undefined, 'json', false)

  if (resp.status === 200) {
    const schoolData: GetSchoolResponse = await resp.json()
    return {
      schoolName: schoolData.name,
      schoolLogo: schoolData.school_logo_url ?? null,
      isRegistrationOpen: schoolData.registration_open,
    }
  }
  throw new Error(`Failed to fetch school details: ${resp.status} ${resp.statusText}`)
}

export interface GetParticipationResponse {
  id: number
  school_name: string
  contact_goal: number
  contribution_goal: string
  goals: APIContributionGoal & APIContactGoal
  contacts: Array<APIContact>
  contributions: Array<APIContribution>
  students: Array<APIStudent>
}

export async function getParticipationById(participationId: string) {
  const resp = await contributionAPI(`participations/${participationId}`)

  if (resp.status === 200) {
    const participationData: GetParticipationResponse = await resp.json()

    return {
      id: participationData.id,
      schoolName: participationData.school_name,
      contactGoal: participationData.contact_goal,
      contributionGoal: participationData.contribution_goal,
      goals: {
        contributionGoal: apiTransformGoal(participationData.goals.contribution_goal),
        contactGoal: apiTransformGoal(participationData.goals.contact_goal),
      },
      contacts: apiTransformContacts(participationData.contacts),
      contributions: apiTransformContributions(participationData.contributions),
      students: apiTransformStudents(participationData.students),
    }
  }
}

export async function fetchPhoto(participationId: string): Promise<string> {
  const resp = await contributionAPI(`participations/${participationId}/photo`)

  if (resp.status === 200) {
    const { photo_url } = await resp.json()

    return photo_url
  }

  throw new Error(`Failed to fetch photo: ${resp.status} ${resp.statusText}`)
}

export async function uploadPhoto(participationId: string, photo: File) {
  const formData = new FormData()
  formData.append('students_photo', photo)

  const resp = await contributionAPI(
    `participations/${participationId}/upload_photo`,
    'access',
    'PATCH',
    formData,
    'form',
  )

  if (resp.status === 200) {
    const data: { photo_url: string } = await resp.json()

    return {
      photoUrl: data.photo_url ?? '',
    }
  }
  throw new Error(`Failed to upload photo: ${resp.status} ${resp.statusText}`)
}

export async function deletePhoto(participationId: string): Promise<boolean> {
  if (!participationId) {
    throw new Error('Participation ID is required to delete a photo.')
  }

  const resp = await contributionAPI(`participations/${participationId}/delete_photo`, 'access', 'PATCH')

  if (resp.status === 200) {
    return true
  }

  throw new Error(`Failed to delete photo: ${resp.status} ${resp.statusText}`)
}

export async function fetchClassroomsByEventId(eventId: string): Promise<Classroom[]> {
  const resp = await contributionAPI(`events/${eventId}/classrooms`)

  if (resp.status === 200) {
    const responseData: APIClassroom[] = await resp.json()
    const transformedClassrooms = apiTransformClassrooms(responseData)

    return transformedClassrooms
  }

  throw new Error(`Failed to fetch classrooms: ${resp.status} ${resp.statusText}`)
}

export interface GetContactsResponse {
  contact_goal: number
  goals: APIContactGoal
  contacts: APIContact[]
}

export async function fetchContactsByParticipationId(
  participationId: string,
): Promise<{ contactGoal: number; goals: { contactGoal: Goal }; contacts: Contact[] }> {
  const resp = await contributionAPI(`participations/${participationId}/contacts`)
  if (resp.status === 200) {
    const responseData: GetContactsResponse = await resp.json()
    const transformedContacts = apiTransformContacts(responseData.contacts)
    const contactGoal = responseData.contact_goal ?? DEFAULT_CONTACT_GOAL
    return {
      contactGoal,
      goals: {
        contactGoal: apiTransformGoalWithFallback(
          responseData.goals?.contact_goal,
          contactGoal,
          transformedContacts.length,
        ),
      },
      contacts: transformedContacts,
    }
  }
  throw new Error(`Failed to fetch contacts: ${resp.status} ${resp.statusText}`)
}

export async function fetchStudentsByParticipationId(participationId: string): Promise<Student[]> {
  const resp = await contributionAPI(`participations/${participationId}/students`)

  if (resp.status === 200) {
    const responseData: APIStudent[] = await resp.json()
    const transformedStudents = apiTransformStudents(responseData)

    return transformedStudents as Student[]
  }
  throw new Error(`Failed to fetch students: ${resp.status} ${resp.statusText}`)
}

export interface CreateStudentArgs {
  firstName: string
  lastName: string
  classroomId: number | null
  participationId: string
}

export async function createStudentForParticipation(studentData: CreateStudentArgs): Promise<Student> {
  const apiStudentData = {
    first_name: studentData.firstName,
    last_name: studentData.lastName,
    teacher_id: studentData.classroomId,
    participation_id: studentData.participationId,
  }

  const resp = await contributionAPI(`students`, 'access', 'POST', JSON.stringify(apiStudentData))

  if (resp.status === 201) {
    const responseData: APIStudent = await resp.json()
    const transformedStudents = apiTransformStudents([responseData])

    if (transformedStudents.length === 0) {
      throw new Error('Failed to transform student response')
    }

    return transformedStudents[0]
  }

  let errorMessage = ''
  try {
    const errorData = await resp.json()
    if (errorData?.error) {
      if (Array.isArray(errorData.error)) {
        errorMessage = errorData.error.join('. ')
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error
      }
    }
  } catch {
    throw new Error("Sorry, we couldn't complete your request.")
  }

  if (errorMessage) {
    throw new Error(errorMessage)
  }

  throw new Error(`Failed to create student: ${resp.status} ${resp.statusText}`)
}

export interface UpdateStudentArgs {
  studentId: string
  firstName: string
  lastName: string
  classroomId: number | null
}

export async function updateStudentForParticipation(studentData: UpdateStudentArgs): Promise<Student> {
  if (!studentData.studentId) {
    throw new Error('Student ID is required to update.')
  }
  const apiStudentData = {
    first_name: studentData.firstName,
    last_name: studentData.lastName,
    teacher_id: studentData.classroomId,
  }
  const resp = await contributionAPI(
    `students/${studentData.studentId}`,
    'access',
    'PUT',
    JSON.stringify(apiStudentData),
  )

  if (resp.status === 200) {
    const responseData: APIStudent = await resp.json()
    const transformedStudents = apiTransformStudents([responseData])

    if (transformedStudents.length === 0) {
      throw new Error('Failed to transform student response')
    }

    return transformedStudents[0] as Student
  }

  let errorMessage = ''
  try {
    const errorData = await resp.json()
    if (errorData?.error) {
      if (Array.isArray(errorData.error)) {
        errorMessage = errorData.error.join('. ')
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error
      }
    }
  } catch {
    throw new Error("Sorry, we couldn't complete your request.")
  }

  if (errorMessage) {
    throw new Error(errorMessage)
  }

  throw new Error(`Failed to update student: ${resp.status} ${resp.statusText}`)
}

export interface DeleteStudentArgs {
  studentId: string
}

export async function deleteStudentForParticipation(studentData: DeleteStudentArgs): Promise<boolean> {
  if (!studentData.studentId) {
    throw new Error('Student ID is required to delete.')
  }

  const resp = await contributionAPI(`students/${studentData.studentId}`, 'access', 'DELETE')

  if (resp.status === 204) {
    return true
  }

  throw new Error(`Failed to delete student: ${resp.status} ${resp.statusText}`)
}

export interface CreateContactArgs {
  participationId: string
  name: string
  email: string
  alias: string
  phone: string
  language: 'en' | 'es'
}

export interface CreateContactApiResponse {
  contact_goal: number
  goals: APIContactGoal
  contacts: APIContact[]
  errors?: CreateContactApiError[]
}

export interface CreateContactResult {
  contactGoal: number
  goals: { contactGoal: Goal }
  contacts: Contact[]
  errors?: CreateContactErrorResult[]
}

export interface CreateContactApiError {
  contact: CreateContactApiErrorContact
  errors: string[]
}

export interface CreateContactErrorResult {
  contact: CreateContactErrorResultContact
  errors: string[]
}

export interface CreateContactApiErrorContact {
  id?: number
  parent_id?: number
  name?: string
  email?: string
  alias?: string
  language?: 'en' | 'es'
  created_at?: string
  updated_at?: string
  donation_reminders_count?: number
  is_parent?: boolean
  email_status?: string
  sms_status?: string
  participation_id?: string
  opt_out_at?: string
  phone_id?: string
  tributary_flow?: string
}

export interface CreateContactErrorResultContact {
  id?: number | null
  parentId?: number | null
  name?: string | null
  email?: string | null
  alias?: string | null
  language?: 'en' | 'es' | null
  createdAt?: string | null
  updatedAt?: string | null
  donationRemindersCount?: number | null
  isParent?: boolean | null
  emailStatus?: string | null
  smsStatus?: string | null
  participationId?: string | null
  optOutAt?: string | null
  phoneId?: string | null
  tributaryFlow?: string | null
}

export async function createContactForParticipation(contactData: CreateContactArgs): Promise<CreateContactResult> {
  const apiContactData = {
    participation_id: contactData.participationId,
    contacts: [
      {
        name: contactData.name,
        email: contactData.email,
        alias: contactData.alias,
        phone: contactData.phone,
        language: contactData.language,
      },
    ],
  }

  const resp = await contributionAPI(`contacts`, 'access', 'POST', JSON.stringify(apiContactData))

  if (resp.status === 200 || resp.status === 201) {
    const responseData: CreateContactApiResponse = await resp.json()
    const transformedResponse = apiTransformCreateContactResponse(responseData)
    return transformedResponse
  }

  throw new Error(`Failed to create contact: ${resp.status} ${resp.statusText}`)
}

export interface BulkCreateContactData {
  participationId: string
  contacts: Array<{
    name: string
    email: string
    alias: string
    phone: string
    language: 'en' | 'es'
  }>
  source?: string
}

export async function bulkCreateContactsForParticipation(
  contactData: BulkCreateContactData,
): Promise<CreateContactResult> {
  if (!contactData.contacts || contactData.contacts.length === 0) {
    throw new Error('At least one contact is required for bulk creation')
  }

  const apiContactData = {
    participation_id: contactData.participationId,
    contacts: contactData.contacts.map((contact) => ({
      name: contact.name,
      email: contact.email,
      alias: contact.alias,
      phone: contact.phone,
      language: contact.language,
    })),
  }

  const resp = await contributionAPI(`contacts`, 'access', 'POST', JSON.stringify(apiContactData))

  if (resp.status === 200 || resp.status === 201) {
    const responseData: CreateContactApiResponse = await resp.json()
    const transformedResponse = apiTransformCreateContactResponse(responseData)
    return transformedResponse
  }

  throw new Error(`Failed to create contacts: ${resp.status} ${resp.statusText}`)
}

export interface UpdateContactArgs {
  contactId?: string
  name: string
  alias: string
  email: string
  phone: string
}

export async function updateContactForParticipation(contactData: UpdateContactArgs): Promise<Contact> {
  if (!contactData.contactId) {
    throw new Error('Contact ID is required to update.')
  }
  const apiContactData = {
    name: contactData.name,
    alias: contactData.alias,
    email: contactData.email,
    phone: contactData.phone,
  }
  const resp = await contributionAPI(
    `contacts/${contactData.contactId}`,
    'access',
    'PUT',
    JSON.stringify(apiContactData),
  )

  if (resp.status === 200) {
    const responseData: APIContact = await resp.json()
    const transformedContacts = apiTransformContacts([responseData])

    if (transformedContacts.length === 0) {
      throw new Error('Failed to transform contact response')
    }

    return transformedContacts[0] as Contact
  }

  throw new Error(`Failed to update contact: ${resp.status} ${resp.statusText}`)
}

export interface DeleteContactArgs {
  contactId: string
}

export async function deleteContactForParticipation(contactData: DeleteContactArgs): Promise<boolean> {
  if (!contactData.contactId) {
    throw new Error('Contact ID is required to delete.')
  }

  const resp = await contributionAPI(`contacts/${contactData.contactId}`, 'access', 'DELETE')

  if (resp.status === 204) {
    return true
  }

  let errorMessage = ''
  try {
    const errorData = await resp.json()
    if (errorData?.error) {
      if (Array.isArray(errorData.error)) {
        errorMessage = errorData.error.join('. ')
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error
      }
    }
  } catch {
    throw new Error("Sorry, we couldn't complete your request.")
  }

  if (errorMessage) {
    throw new Error(errorMessage)
  }

  throw new Error(`Failed to delete contact: ${resp.status} ${resp.statusText}`)
}

export interface GetContributionsResponse {
  contribution_goal: string
  goals: APIContributionGoal
  disable_date: ISODate
  contributions: APIContribution[]
}

export async function fetchContributionsByParticipationId(participationId: string) {
  const resp = await contributionAPI(`participations/${participationId}/contributions`)
  if (resp.status === 200) {
    const responseData: GetContributionsResponse = await resp.json()
    const transformedContributions = apiTransformContributions(responseData.contributions)
    const contributionGoal = responseData.contribution_goal ?? DEFAULT_CONTRIBUTION_GOAL
    // Fallback progress mirrors the retired flag-off path: the sum of contribution amounts.
    const contributedAmount = transformedContributions.reduce((sum, c) => sum + Number(c.amount), 0)

    return {
      contributionGoal,
      goals: {
        contributionGoal: apiTransformGoalWithFallback(
          responseData.goals?.contribution_goal,
          Number.parseFloat(String(contributionGoal)),
          contributedAmount,
        ),
      },
      disableDate: responseData.disable_date,
      contributions: transformedContributions,
    }
  }
  throw new Error(`Failed to fetch contributions: ${resp.status} ${resp.statusText}`)
}

export const exampleParticipationData = {
  id: 449,
  school_name: 'Niwot Elementary School',
  contact_goal: 10,
  contribution_goal: '240.0',
  goals: {
    contribution_goal: {
      strategy: 'milestone',
      base_target: 240.0,
      milestones: [300.0, 350.0],
      current_milestone: 240.0,
      next_milestone: 300.0,
      progress: 260.0,
      source: 'event',
    },
    contact_goal: {
      strategy: 'static',
      target: 10,
      progress: 0,
      source: 'event',
    },
  },
  contacts: [
    {
      id: 1231,
      name: 'flavia hernandez',
      alias: null,
      email: 'maayala17+flavia@gmail.com',
      email_status: 'link_opened',
      sms_status: null,
      phone: null,
    },
    {
      id: 1233,
      name: 'Ori Maayala',
      alias: null,
      email: 'maayala17+ori@gmail.com',
      email_status: 'unsubscribe',
      sms_status: 'unsubscribed',
      phone: null,
    },
    {
      id: 1232,
      name: 'Daniela',
      alias: 'Dani',
      email: 'Agustina.ayala+daniela@deviq.io',
      email_status: 'pending',
      sms_status: null,
      phone: null,
    },
  ],
  contributions: [
    {
      amount: '10.0',
      created_at: '2025-09-09T19:35:46.588Z',
      id: 'donation_230',
      name: 'flavia hernandez',
    },
    {
      amount: '30.0',
      created_at: '2025-09-09T19:48:49.804Z',
      id: 'offline_donation_86',
      name: 'Grandpa Williams',
    },
  ],
  students: [
    {
      id: 1391,
      first_name: 'Brendan',
      last_name: 'Foster',
      grade: null,
      classroom: null,
    },
    {
      id: 1436,
      first_name: 'Lisa',
      last_name: 'Simpson',
      grade: null,
      classroom: 'Teacher',
    },
  ],
}
